import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/api";
import { obtenerTokenVigente } from "../api/session";

// ─── Configuracion por tipo ───────────────────────────────────────────────────
const TYPE_CONFIG = {
  provider_match:   { icon: "⚡", color: "#E7A432" },
  quote_received:   { icon: "📋", color: "#163244" },
  quote_accepted:   { icon: "✓",  color: "#1b7f4e" },
  job_completed:    { icon: "🎉", color: "#1b7f4e" },
  request_assigned: { icon: "🔧", color: "#163244" },
  job_reminder:     { icon: "⏰", color: "#b06a00" },
};

function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr)) / 1000;
  if (diff < 60)    return "ahora";
  if (diff < 3600)  return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

// ─── Sonido de campana via Web Audio API ─────────────────────────────────────
function playBell() {
  try {
    const ctx  = new (window.AudioContext || window.webkitAudioContext)();
    const osc  = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type            = "sine";
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.35, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 1.2);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 1.2);
  } catch {}
}

export default function NotificationsBell() {
  const navigate = useNavigate();
  const [open,          setOpen]          = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread,        setUnread]        = useState(0);
  const [loading,       setLoading]       = useState(false);
  const [shake,         setShake]         = useState(false);
  const dropdownRef  = useRef(null);
  const prevUnread   = useRef(null);
  const sseRef       = useRef(null);

  // Cerrar al hacer click fuera
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ─── SSE — conexión en tiempo real ───────────────────────────────────────
  // EventSource no pasa por axios: no manda headers y no ve el status de un
  // 401. Antes reconectaba cada ~11 s con el mismo token vencido, para
  // siempre. Ahora pide un token vigente antes de cada conexión (renueva si le
  // quedan menos de 30 s) y deja de reconectar si la sesión terminó; vuelve a
  // conectar cuando llegan tokens nuevos (re-login, evento 'fixo:tokens').
  const reconexionRef = useRef(null);
  const montadoRef    = useRef(true);

  const connectSSE = useCallback(async () => {
    clearTimeout(reconexionRef.current);

    let token;
    try {
      token = await obtenerTokenVigente();
    } catch {
      // Refresh caído por red: reintentar más tarde, la sesión puede seguir.
      if (montadoRef.current) reconexionRef.current = setTimeout(connectSSE, 10000);
      return;
    }
    if (!token || !montadoRef.current) return; // sesión terminada o desmontado

    // Cerrar conexión anterior si existe
    if (sseRef.current) sseRef.current.close();

    const apiBase = process.env.REACT_APP_API_URL || "";
    const es = new EventSource(`${apiBase}/notifications/stream?token=${token}`);
    sseRef.current = es;

    es.addEventListener("update", (e) => {
      try {
        const data = JSON.parse(e.data);
        const newCount = data.count ?? 0;

        // Si hay más no leídas que antes → sonar campana + animar
        if (prevUnread.current !== null && newCount > prevUnread.current) {
          playBell();
          setShake(true);
          setTimeout(() => setShake(false), 600);

          // Si hay una notif nueva, actualizar el dropdown si está abierto
          if (data.latest) {
            setNotifications(prev => {
              const exists = prev.some(n => n.id === data.latest.id);
              if (exists) return prev;
              return [{ ...data.latest, read: false }, ...prev];
            });
          }
        }

        prevUnread.current = newCount;
        setUnread(newCount);
      } catch {}
    });

    es.onerror = () => {
      es.close();
      if (sseRef.current === es) sseRef.current = null;
      // Reconectar en 10s si se cae la conexión
      if (montadoRef.current) reconexionRef.current = setTimeout(connectSSE, 10000);
    };
  }, []);

  useEffect(() => {
    montadoRef.current = true;
    connectSSE();
    const alRenovar = () => { if (!sseRef.current) connectSSE(); };
    window.addEventListener("fixo:tokens", alRenovar);
    return () => {
      montadoRef.current = false;
      clearTimeout(reconexionRef.current);
      window.removeEventListener("fixo:tokens", alRenovar);
      if (sseRef.current) sseRef.current.close();
      sseRef.current = null;
    };
  }, [connectSSE]);

  // Cargar notificaciones al abrir
  const handleOpen = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    setLoading(true);
    try {
      const res = await api.get("/notifications?limit=20");
      setNotifications(res.data.notifications || []);
    } catch {}
    finally { setLoading(false); }
  };

  const handleMarkRead = async (notif) => {
    if (!notif.read) {
      await api.patch(`/notifications/${notif.id}/read`).catch(() => {});
      setNotifications(ns => ns.map(n => n.id === notif.id ? { ...n, read: true } : n));
      setUnread(u => Math.max(0, u - 1));
    }
    if (notif.link) {
      setOpen(false);
      navigate(notif.link);
    }
  };

  const handleMarkAllRead = async () => {
    await api.patch("/notifications/read-all").catch(() => {});
    setNotifications(ns => ns.map(n => ({ ...n, read: true })));
    setUnread(0);
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    await api.delete(`/notifications/${id}`).catch(() => {});
    setNotifications(ns => ns.filter(n => n.id !== id));
  };

  return (
    <div ref={dropdownRef} style={{ position: "relative" }}>

      {/* Campanita */}
      <button
        onClick={handleOpen}
        style={{
          position: "relative",
          background: open ? "rgba(255,255,255,0.15)" : "transparent",
          border: "none", cursor: "pointer",
          width: 36, height: 36, borderRadius: "50%",
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "background 0.2s",
          animation: shake ? "bellShake 0.5s ease" : "none",
        }}
        title="Notificaciones"
      >
        <span style={{ fontSize: 18 }}>🔔</span>
        {unread > 0 && (
          <span style={{
            position: "absolute", top: 2, right: 2,
            background: "#c0392b", color: "#fff",
            fontSize: 9, fontWeight: 800,
            width: 16, height: 16, borderRadius: "50%",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: "Montserrat, sans-serif",
          }}>
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: "absolute", right: 0, top: "calc(100% + 8px)",
          width: 340, maxHeight: 480,
          background: "#fff", borderRadius: 12,
          boxShadow: "0 8px 32px rgba(22,50,68,0.18)",
          border: "1px solid #e0e9ef",
          zIndex: 9999, overflow: "hidden",
          display: "flex", flexDirection: "column",
          fontFamily: "Montserrat, sans-serif",
        }}>

          {/* Header del dropdown */}
          <div style={{
            padding: "14px 16px",
            borderBottom: "1px solid #f0f5f8",
            display: "flex", justifyContent: "space-between", alignItems: "center",
          }}>
            <div style={{ fontWeight: 800, fontSize: 14, color: "#163244" }}>
              Notificaciones
              {unread > 0 && (
                <span style={{
                  marginLeft: 8, background: "#c0392b", color: "#fff",
                  fontSize: 10, fontWeight: 700, padding: "1px 7px",
                  borderRadius: 10,
                }}>
                  {unread} nueva{unread !== 1 ? "s" : ""}
                </span>
              )}
            </div>
            {unread > 0 && (
              <button onClick={handleMarkAllRead} style={{
                background: "none", border: "none", cursor: "pointer",
                fontSize: 11, color: "#163244", fontWeight: 600,
                fontFamily: "Montserrat, sans-serif", textDecoration: "underline",
              }}>
                Marcar todas leidas
              </button>
            )}
          </div>

          {/* Lista */}
          <div style={{ overflowY: "auto", flex: 1 }}>
            {loading && (
              <div style={{ padding: "32px", textAlign: "center", color: "#8fa3b1", fontSize: 13 }}>
                <span style={{
                  display: "inline-block", width: 18, height: 18,
                  border: "2px solid #cddae4", borderTopColor: "#163244",
                  borderRadius: "50%", animation: "spin 0.7s linear infinite",
                }} />
              </div>
            )}

            {!loading && notifications.length === 0 && (
              <div style={{ padding: "40px 20px", textAlign: "center", color: "#8fa3b1" }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>🔔</div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>Sin notificaciones</div>
              </div>
            )}

            {!loading && notifications.map(notif => {
              const cfg = TYPE_CONFIG[notif.type] || { icon: "📢", color: "#163244" };
              return (
                <div
                  key={notif.id}
                  onClick={() => handleMarkRead(notif)}
                  style={{
                    display: "flex", gap: 10, padding: "12px 16px",
                    borderBottom: "1px solid #f0f5f8",
                    background: notif.read ? "#fff" : "#f0f5f8",
                    cursor: notif.link ? "pointer" : "default",
                    transition: "background 0.15s",
                    alignItems: "flex-start",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = "#e7f0f5"}
                  onMouseLeave={e => e.currentTarget.style.background = notif.read ? "#fff" : "#f0f5f8"}
                >
                  <div style={{
                    width: 34, height: 34, borderRadius: "50%", flexShrink: 0,
                    background: `${cfg.color}18`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 16,
                  }}>
                    {cfg.icon}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontWeight: notif.read ? 600 : 800,
                      fontSize: 12, color: "#163244", marginBottom: 2,
                    }}>
                      {notif.title}
                    </div>
                    <div style={{
                      fontSize: 11, color: "#5c7a8a", lineHeight: 1.5,
                      overflow: "hidden", textOverflow: "ellipsis",
                      display: "-webkit-box", WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}>
                      {notif.body}
                    </div>
                    <div style={{ fontSize: 10, color: "#aab8c2", marginTop: 3 }}>
                      {timeAgo(notif.created_at)}
                    </div>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, flexShrink: 0 }}>
                    {!notif.read && (
                      <div style={{
                        width: 7, height: 7, borderRadius: "50%",
                        background: "#163244", marginTop: 4,
                      }} />
                    )}
                    <button
                      onClick={e => handleDelete(e, notif.id)}
                      style={{
                        background: "none", border: "none", cursor: "pointer",
                        color: "#cddae4", fontSize: 14, padding: 0, lineHeight: 1,
                      }}
                      title="Eliminar"
                    >
                      ×
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes bellShake {
          0%   { transform: rotate(0deg);   }
          20%  { transform: rotate(-15deg); }
          40%  { transform: rotate(15deg);  }
          60%  { transform: rotate(-10deg); }
          80%  { transform: rotate(10deg);  }
          100% { transform: rotate(0deg);   }
        }
      `}</style>
    </div>
  );
}
